export const M3_UNIT_14 = {
  id: "m3-u14-modifiers-connectors",
  title: "Unit 14: Modifiers & Connectors",
  topic: "Master intensifiers (very, quite, really) and connect ideas like a native with because, so, and but. A2→B1 leap!",
  iconType: "zap",
  grammarImage: "/m3-u14-modifiers.jpg",
  grammarText: "Learn to intensify your speech with modifiers and build complex sentences using connectors. Integrate abilities, can/can't, and adverbs into native-level expressions. Real B1 English for jobs and life.",
  sets: [
    // ===================================================================
    // SET 1: Very (básico) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u14-set1",
      title: "Very - Intensifier",
      questions: [
        {
          type: "mc",
          q: "I'm _____ tired today. I worked 12 hours!",
          options: ["very", "quite", "much"],
          a: "very",
          tip: "'Very' = MUY! Most common intensifier in English!",
          successtip: "✓ VERY tired! Strongest basic intensifier! 💤 Work reality: 12 hours = VERY tired! Universal expression!"
        },
        {
          type: "mc",
          q: "She speaks English _____ well. I understand everything!",
          options: ["very", "good", "much"],
          a: "very",
          tip: "'Very' modifies adverbs! Very + well = muy bien!",
          successtip: "✓ VERY well! Modifier + adverb! 🗣️ Job skill: 'I speak English very well' = interview gold!"
        },
        {
          type: "mc",
          q: "The food is _____ good here. I eat here every day!",
          options: ["very", "much", "many"],
          a: "very",
          tip: "'Very' + adjective! Very good = muy bueno!",
          successtip: "✓ VERY good! Basic but essential! 🍽️ Restaurant praise! Very = strong but neutral!"
        },
        {
          type: "mc",
          q: "I walk _____ slowly when I'm tired.",
          options: ["very", "much", "too much"],
          a: "very",
          tip: "'Very' + adverb of manner! Intensifies HOW you walk!",
          successtip: "✓ VERY slowly! Modifier + adverb! 🚶 Tired = walk very slowly! Natural combination!"
        },
        {
          type: "mc",
          q: "My boss is _____ strict. We work carefully!",
          options: ["very", "much", "many"],
          a: "very",
          tip: "'Very' + adjective describing personality!",
          successtip: "✓ VERY strict! Workplace reality! 💼 Strict boss = very careful work! Survival tip!"
        },
        {
          type: "mc",
          q: "Thank you _____ much for your help!",
          options: ["very", "quite", "really"],
          a: "very",
          tip: "'Thank you very much' = fixed expression! Super polite!",
          successtip: "✓ VERY much! Fixed phrase! 🙏 Formal gratitude: 'Thank you very much!' Customer service essential!"
        },
        {
          type: "mc",
          q: "I'm _____ hungry. I didn't eat breakfast!",
          options: ["very", "much", "many"],
          a: "very",
          tip: "'Very' + adjective for feelings and states!",
          successtip: "✓ VERY hungry! Physical state! 🍔 No breakfast = very hungry! Real cause-effect!"
        },
        {
          type: "mc",
          q: "She types _____ quickly. She's a professional!",
          options: ["very", "much", "good"],
          a: "very",
          tip: "'Very' + adverb! Very quickly = muy rápidamente!",
          successtip: "✓ VERY quickly! Office skills! ⌨️ Fast typer = types very quickly! Job requirement!"
        },
        {
          type: "mc",
          q: "The weather is _____ cold today. Wear a jacket!",
          options: ["very", "much", "many"],
          a: "very",
          tip: "'Very' + adjective for weather!",
          successtip: "✓ VERY cold! Weather talk! 🥶 Canadian winters = very cold! Practical advice!"
        },
        {
          type: "mc",
          q: "I'm _____ happy with my new job!",
          options: ["very", "much", "many"],
          a: "very",
          tip: "'Very' + emotion adjective!",
          successtip: "✓ VERY happy! Emotions! 😊 New job = very happy! Life milestone!"
        }
      ]
    },

    // ===================================================================
    // SET 2: Really (énfasis/conversational) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u14-set2",
      title: "Really - Emphasis",
      questions: [
        {
          type: "mc",
          q: "I'm _____ sorry! I didn't mean it!",
          options: ["really", "very", "much"],
          a: "really",
          tip: "'Really sorry' = super sincere! More emotional than 'very sorry'!",
          successtip: "✓ REALLY sorry! Sincere apology! 🙏 Workplace mistake: 'I'm really sorry!' shows genuine regret!"
        },
        {
          type: "mc",
          q: "This job is _____ hard! I work 10 hours every day!",
          options: ["really", "much", "many"],
          a: "really",
          tip: "'Really' = emphasis + emotion! Stronger feeling than 'very'!",
          successtip: "✓ REALLY hard! Immigrant grind! 💪 10 hours = really hard! Emphasizes struggle!"
        },
        {
          type: "mc",
          q: "She speaks English _____ well! Almost like a native!",
          options: ["really", "much", "good"],
          a: "really",
          tip: "'Really well' = impressively well! Shows admiration!",
          successtip: "✓ REALLY well! High praise! 🌟 Almost native = speaks really well! Achievement recognition!"
        },
        {
          type: "mc",
          q: "I can't hear you. Can you speak _____ loudly?",
          options: ["really", "much", "too"],
          a: "really",
          tip: "'Really loudly' = very very loud! Extra emphasis!",
          successtip: "✓ REALLY loudly! Emphasis request! 📢 Can't hear = need really loud! Practical command!"
        },
        {
          type: "mc",
          q: "That's _____ good news! Congratulations!",
          options: ["really", "much", "many"],
          a: "really",
          tip: "'Really good' = excellent! Shows excitement!",
          successtip: "✓ REALLY good! Excited reaction! 🎉 Good news deserves 'really'! Emotional response!"
        },
        {
          type: "mc",
          q: "I'm _____ tired after the night shift.",
          options: ["really", "much", "many"],
          a: "really",
          tip: "'Really tired' = exhausted! Stronger than 'very tired'!",
          successtip: "✓ REALLY tired! Night shift reality! 😴 Exhaustion = really tired! Worker struggle!"
        },
        {
          type: "mc",
          q: "She cooks _____ well. The food is delicious!",
          options: ["really", "much", "good"],
          a: "really",
          tip: "'Really well' = exceptionally well! High compliment!",
          successtip: "✓ REALLY well! Chef praise! 👨‍🍳 Delicious food = cooks really well! Quality recognition!"
        },
        {
          type: "mc",
          q: "I'm _____ happy you came! Thank you!",
          options: ["really", "much", "many"],
          a: "really",
          tip: "'Really happy' = very very happy! Warm emotion!",
          successtip: "✓ REALLY happy! Genuine emotion! 😊 Visitor welcome = really happy! Heartfelt expression!"
        },
        {
          type: "mc",
          q: "Is that _____ true?! I don't believe it!",
          options: ["really", "very", "much"],
          a: "really",
          tip: "'Really?' = common question showing surprise!",
          successtip: "✓ REALLY true?! Surprise question! 😲 'Really?' = universal reaction! Conversational essential!"
        },
        {
          type: "mc",
          q: "The boss is _____ strict. Everyone is scared!",
          options: ["really", "much", "many"],
          a: "really",
          tip: "'Really strict' = extremely strict! Emphasizes severity!",
          successtip: "✓ REALLY strict! Office fear! 😰 Scary boss = really strict! Workplace reality!"
        }
      ]
    },

    // ===================================================================
    // SET 3: Quite (moderado, UK) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u14-set3",
      title: "Quite - Moderate Level",
      questions: [
        {
          type: "mc",
          q: "I can speak English _____ well. Not perfect, but good!",
          options: ["quite", "quiet", "much"],
          a: "quite",
          tip: "'Quite well' = bastante bien! Moderate, not extreme!",
          successtip: "✓ QUITE well! Honest self-assessment! 📊 Not perfect but quite well = realistic! Job interview honesty!"
        },
        {
          type: "mc",
          q: "She's _____ good at tennis. She wins sometimes!",
          options: ["quite", "quiet", "much"],
          a: "quite",
          tip: "'Quite good' = fairly good! Shows moderate skill!",
          successtip: "✓ QUITE good! Middle level! 🎾 Wins sometimes = quite good! Not pro, but skilled!"
        },
        {
          type: "mc",
          q: "It's _____ cold today. I need a light jacket.",
          options: ["quite", "quiet", "much"],
          a: "quite",
          tip: "'Quite cold' = somewhat cold! Not freezing, but cold!",
          successtip: "✓ QUITE cold! Moderate weather! 🧥 Light jacket = quite cold! Practical description!"
        },
        {
          type: "mc",
          q: "I'm _____ tired, but I can work.",
          options: ["quite", "quiet", "much"],
          a: "quite",
          tip: "'Quite tired' = moderately tired! Not exhausted!",
          successtip: "✓ QUITE tired! Still functional! 💼 Can still work = quite tired! Working parent reality!"
        },
        {
          type: "mc",
          q: "He runs _____ fast for his age.",
          options: ["quite", "quiet", "much"],
          a: "quite",
          tip: "'Quite fast' = fairly fast! Positive moderate!",
          successtip: "✓ QUITE fast! Age-appropriate! 🏃 For his age = quite fast! Relative comparison!"
        },
        {
          type: "mc",
          q: "The job is _____ difficult, but I can learn.",
          options: ["quite", "quiet", "much"],
          a: "quite",
          tip: "'Quite difficult' = somewhat difficult! Challenging but manageable!",
          successtip: "✓ QUITE difficult! Manageable challenge! 📚 Can learn = quite difficult but okay! Growth mindset!"
        },
        {
          type: "mc",
          q: "I understand English _____ well now!",
          options: ["quite", "quiet", "much"],
          a: "quite",
          tip: "'Quite well' = pretty well! Shows progress!",
          successtip: "✓ QUITE well! Learning progress! 📈 'Now' = improvement! Learner pride!"
        },
        {
          type: "mc",
          q: "She types _____ quickly. Not super fast, but okay!",
          options: ["quite", "quiet", "much"],
          a: "quite",
          tip: "'Quite quickly' = reasonably fast! Acceptable speed!",
          successtip: "✓ QUITE quickly! Adequate skill! ⌨️ Okay speed = quite quickly! Job competency!"
        },
        {
          type: "mc",
          q: "I'm _____ good at cooking. I learned from my mom!",
          options: ["quite", "quiet", "much"],
          a: "quite",
          tip: "'Quite good' = fairly skilled! Modest but positive!",
          successtip: "✓ QUITE good! Family skill! 👩‍🍳 Mom's teaching = quite good! Cultural pride!"
        },
        {
          type: "mc",
          q: "The bus is _____ slow today. There's traffic.",
          options: ["quite", "quiet", "much"],
          a: "quite",
          tip: "'Quite slow' = somewhat slow! Noticeable but not terrible!",
          successtip: "✓ QUITE slow! Commute reality! 🚌 Traffic = quite slow! Daily frustration!"
        }
      ]
    },

    // ===================================================================
    // SET 4: Good at + Modifiers + Can (10 preguntas)
    // ===================================================================
    {
      id: "m3-u14-set4",
      title: "Good at + Modifiers",
      questions: [
        {
          type: "mc",
          q: "I'm good at swimming. I can swim _____ well.",
          options: ["quite", "quiet", "good"],
          a: "quite",
          tip: "'Good at' + 'can' + modifier! Shows skill level!",
          successtip: "✓ QUITE well! B1 integration! 🏊 Good at = can do quite well! Native expression!"
        },
        {
          type: "mc",
          q: "She's _____ good at English. She practices every day!",
          options: ["really", "much", "good"],
          a: "really",
          tip: "'Really good at' = very skilled! High praise!",
          successtip: "✓ REALLY good! Daily practice result! 📚 Practice = really good! Dedication pays!"
        },
        {
          type: "mc",
          q: "I'm good at cooking. I can cook _____ well for my family.",
          options: ["quite", "quiet", "good"],
          a: "quite",
          tip: "Good at + can + quite well = moderate confidence!",
          successtip: "✓ QUITE well! Family chef! 👨‍🍳 Good at cooking = feed family quite well! Practical skill!"
        },
        {
          type: "mc",
          q: "He's not _____ good at math. He's still learning.",
          options: ["very", "much", "many"],
          a: "very",
          tip: "'Not very good' = below average! Honest assessment!",
          successtip: "✓ NOT VERY good! Learning phase! 📊 Still learning = not very good YET! Growth potential!"
        },
        {
          type: "mc",
          q: "I'm _____ good at customer service. Customers love me!",
          options: ["really", "much", "many"],
          a: "really",
          tip: "'Really good at' + proof! Customers love = evidence!",
          successtip: "✓ REALLY good! Customer feedback! 💼 Love me = really good at service! Tips proof!"
        },
        {
          type: "mc",
          q: "She's good at tennis. She can play _____ well.",
          options: ["quite", "quiet", "good"],
          a: "quite",
          tip: "Good at sport + can play quite well = moderate skill!",
          successtip: "✓ QUITE well! Sports ability! 🎾 Good at = can play quite well! Skill description!"
        },
        {
          type: "mc",
          q: "I'm not good at driving. I drive _____ slowly.",
          options: ["very", "much", "good"],
          a: "very",
          tip: "Not good at + drive very slowly = nervous driver!",
          successtip: "✓ VERY slowly! Cautious driver! 🚗 Not good = drive very slowly! Safety first!"
        },
        {
          type: "mc",
          q: "He's _____ good at his job. The boss loves his work!",
          options: ["really", "much", "many"],
          a: "really",
          tip: "'Really good' + boss approval = high performance!",
          successtip: "✓ REALLY good! Boss praise! 👔 Boss loves work = really good! Job security!"
        },
        {
          type: "mc",
          q: "I'm good at reading English, but I can't speak _____ well.",
          options: ["very", "much", "good"],
          a: "very",
          tip: "'Can't + very well' = skill gap! Common learner issue!",
          successtip: "✓ NOT VERY well! Common gap! 📖 Read ≠ speak! Every learner's struggle!"
        },
        {
          type: "mc",
          q: "She's _____ good at languages. She speaks five!",
          options: ["really", "much", "many"],
          a: "really",
          tip: "'Really good' + proof! Five languages = exceptional!",
          successtip: "✓ REALLY good! Polyglot! 🌍 Five languages = really good talent! Impressive!"
        }
      ]
    },

    // ===================================================================
    // SET 5: Bad at + Modifiers + Can't (10 preguntas)
    // ===================================================================
    {
      id: "m3-u14-set5",
      title: "Bad at + Modifiers",
      questions: [
        {
          type: "mc",
          q: "I'm bad at skiing. I can't coordinate _____ well.",
          options: ["very", "much", "good"],
          a: "very",
          tip: "Bad at + can't + very well = explanation!",
          successtip: "✓ CAN'T coordinate VERY well! B1 reasoning! 🎿 Bad at skiing = can't coordinate! Logical connection!"
        },
        {
          type: "mc",
          q: "She's _____ bad at math. She fails every test!",
          options: ["really", "much", "many"],
          a: "really",
          tip: "'Really bad' = terrible! Emphasis on struggle!",
          successtip: "✓ REALLY bad! School struggle! 📐 Fail tests = really bad at math! Academic difficulty!"
        },
        {
          type: "mc",
          q: "I'm bad at singing. I can't sing _____ well at all!",
          options: ["very", "much", "good"],
          a: "very",
          tip: "'Can't + very well' = poor ability! Honest admission!",
          successtip: "✓ CAN'T sing VERY well! Musical honesty! 🎤 Bad at singing = can't do it well! Self-awareness!"
        },
        {
          type: "mc",
          q: "He's _____ bad at sports. He never plays.",
          options: ["quite", "quiet", "much"],
          a: "quite",
          tip: "'Quite bad' = moderately bad! Not terrible, but not good!",
          successtip: "✓ QUITE bad! Sports challenge! ⚽ Never plays = quite bad! Preference avoidance!"
        },
        {
          type: "mc",
          q: "I'm bad at dancing. I can't move _____ gracefully.",
          options: ["very", "much", "good"],
          a: "very",
          tip: "Bad at + can't + very + adverb = detailed weakness!",
          successtip: "✓ CAN'T move VERY gracefully! Dancing struggle! 💃 Bad at dancing = ungraceful! B1 detail!"
        },
        {
          type: "mc",
          q: "She's not _____ bad at English. She's learning!",
          options: ["very", "much", "many"],
          a: "very",
          tip: "'Not very bad' = not terrible! Encouraging!",
          successtip: "✓ NOT VERY bad! Progress view! 📚 Learning = not very bad! Positive spin!"
        },
        {
          type: "mc",
          q: "I'm _____ bad at remembering names. I forget quickly!",
          options: ["really", "much", "many"],
          a: "really",
          tip: "'Really bad at' + reason! Emphasizes weakness!",
          successtip: "✓ REALLY bad! Memory struggle! 🧠 Forget quickly = really bad at names! Common problem!"
        },
        {
          type: "mc",
          q: "He's bad at computers. He types _____ slowly.",
          options: ["very", "much", "good"],
          a: "very",
          tip: "Bad at + types very slowly = connected skills!",
          successtip: "✓ Types VERY slowly! Tech struggle! 💻 Bad at computers = slow typing! Logical link!"
        },
        {
          type: "mc",
          q: "I'm bad at waking up early. I'm _____ tired in the morning!",
          options: ["really", "much", "many"],
          a: "really",
          tip: "Bad at habit + really tired = consequence!",
          successtip: "✓ REALLY tired! Morning struggle! 😴 Bad at waking = really tired! Night owl reality!"
        },
        {
          type: "mc",
          q: "She's _____ bad at parking. She hits things!",
          options: ["quite", "quiet", "much"],
          a: "quite",
          tip: "'Quite bad' + proof! Hits things = evidence!",
          successtip: "✓ QUITE bad! Parking nightmare! 🚗 Hits things = quite bad at parking! Driver's fear!"
        }
      ]
    },

    // ===================================================================
    // SET 6: BECAUSE - Cause & Effect (10 preguntas B1)
    // ===================================================================
    {
      id: "m3-u14-set6",
      title: "BECAUSE - Reasoning",
      questions: [
        {
          type: "mc",
          q: "I'm good at English _____ I practice every day.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains the REASON! Why are you good?",
          successtip: "✓ BECAUSE! B1 reasoning! 🧠 Practice = reason for skill! Cause-effect logic!"
        },
        {
          type: "mc",
          q: "I can't sleep well _____ I work the night shift.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' shows cause! Night shift = reason for bad sleep!",
          successtip: "✓ BECAUSE! Worker reality! 😴 Night shift causes sleep problems! Real immigrant struggle!"
        },
        {
          type: "mc",
          q: "She speaks really well _____ she watches TV in English.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains HOW she learned! TV = method!",
          successtip: "✓ BECAUSE! Learning method! 📺 TV watching = speaks well! Effective strategy!"
        },
        {
          type: "mc",
          q: "I'm bad at skiing _____ I can't coordinate very well.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains weakness! Can't coordinate = reason!",
          successtip: "✓ BECAUSE! Physical reason! 🎿 Poor coordination = bad at skiing! Logical explanation!"
        },
        {
          type: "mc",
          q: "I'm really tired _____ I worked 12 hours today.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' shows cause! 12 hours = reason for tiredness!",
          successtip: "✓ BECAUSE! Work exhaustion! 💪 Long hours cause tiredness! Hustle reality!"
        },
        {
          type: "mc",
          q: "She can cook quite well _____ her mother taught her.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' shows origin of skill! Mother = source!",
          successtip: "✓ BECAUSE! Family skill! 👩‍🍳 Mother's teaching = cooking ability! Cultural transmission!"
        },
        {
          type: "mc",
          q: "I can't drive fast _____ I'm very nervous in traffic.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains limitation! Nervousness = reason!",
          successtip: "✓ BECAUSE! Anxiety reason! 🚗 Nervous = drive slowly! Honest self-awareness!"
        },
        {
          type: "mc",
          q: "He's really good at his job _____ he works very carefully.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains success! Careful work = reason!",
          successtip: "✓ BECAUSE! Work quality! 💼 Careful work = good performance! Success formula!"
        },
        {
          type: "mc",
          q: "I understand English quite well _____ I read every day.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' shows learning method! Reading = cause!",
          successtip: "✓ BECAUSE! Reading habit! 📚 Daily reading = understanding! Learning strategy!"
        },
        {
          type: "mc",
          q: "She's bad at math _____ she doesn't understand numbers well.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains weakness! Lack of understanding = reason!",
          successtip: "✓ BECAUSE! Academic struggle! 📐 Number confusion = bad at math! Root cause!"
        }
      ]
    },

    // ===================================================================
    // SET 7: SO - Consequence (10 preguntas B1)
    // ===================================================================
    {
      id: "m3-u14-set7",
      title: "SO - Consequence",
      questions: [
        {
          type: "mc",
          q: "I practice every day, _____ I can speak quite well now.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' shows RESULT! Practice → speak well!",
          successtip: "✓ SO! Result logic! 💡 Practice leads to skill! Action → consequence!"
        },
        {
          type: "mc",
          q: "I'm really tired, _____ I walk very slowly today.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' shows consequence! Tired → walk slowly!",
          successtip: "✓ SO! Physical consequence! 😴 Tiredness affects movement! Cause → effect!"
        },
        {
          type: "mc",
          q: "She's good at cooking, _____ she works in a restaurant.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' shows outcome! Skill → job!",
          successtip: "✓ SO! Career path! 👨‍🍳 Cooking skill = restaurant job! Ability → opportunity!"
        },
        {
          type: "mc",
          q: "I can't coordinate well, _____ I'm bad at sports.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' shows result! Poor coordination → bad at sports!",
          successtip: "✓ SO! Physical limitation! ⚽ Can't coordinate = bad at sports! Logic chain!"
        },
        {
          type: "mc",
          q: "He works very hard, _____ he's really good at his job.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' shows achievement! Hard work → good performance!",
          successtip: "✓ SO! Work ethic pays! 💼 Hard work = job excellence! Success formula!"
        },
        {
          type: "mc",
          q: "I watch TV in English, _____ I understand quite well now.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' shows learning result! TV → understanding!",
          successtip: "✓ SO! Learning outcome! 📺 TV practice = comprehension! Study method works!"
        },
        {
          type: "mc",
          q: "She's very nervous, _____ she speaks quite quietly.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' shows behavioral result! Nervous → quiet speech!",
          successtip: "✓ SO! Emotional effect! 😰 Nervousness = quiet speaking! Psychology in action!"
        },
        {
          type: "mc",
          q: "I can't speak well, _____ I practice every day.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' shows CONTRAST! Can't speak YET but practice!",
          successtip: "✓ BUT! Contrast! 📚 Current weakness BUT future hope! Growth mindset!"
        },
        {
          type: "mc",
          q: "I'm good at my job, _____ my boss is very happy.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' shows outcome! Good work → happy boss!",
          successtip: "✓ SO! Workplace success! 👔 Good performance = happy boss! Job security!"
        },
        {
          type: "mc",
          q: "He can't drive, _____ he takes the bus every day.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' shows solution! Can't drive → takes bus!",
          successtip: "✓ SO! Transportation solution! 🚌 No driving = bus commute! Practical consequence!"
        }
      ]
    },

    // ===================================================================
    // SET 8: BUT - Contrast (10 preguntas B1)
    // ===================================================================
    {
      id: "m3-u14-set8",
      title: "BUT - Contrast",
      questions: [
        {
          type: "mc",
          q: "I can read English quite well, _____ I can't speak very fast.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' shows DIFFERENCE between two skills!",
          successtip: "✓ BUT! Skill contrast! 📖 Read well BUT speak slowly! Common learner gap!"
        },
        {
          type: "mc",
          q: "She's really tired, _____ she works very carefully.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' shows contrast! Tired BUT still careful!",
          successtip: "✓ BUT! Work dedication! 😴 Tired BUT maintains quality! Professional attitude!"
        },
        {
          type: "mc",
          q: "I'm bad at math, _____ I'm good at English.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' contrasts abilities! Bad at one, good at another!",
          successtip: "✓ BUT! Different strengths! 📚 Everyone has different skills! Balanced view!"
        },
        {
          type: "mc",
          q: "He can cook quite well, _____ he doesn't like cooking.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' shows contrast! Ability ≠ preference!",
          successtip: "✓ BUT! Skill vs preference! 👨‍🍳 Can cook BUT doesn't like! Ability ≠ enjoyment!"
        },
        {
          type: "mc",
          q: "I practice every day, _____ I'm still quite bad at tennis.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' shows unexpected result! Practice BUT still bad!",
          successtip: "✓ BUT! Slow progress! 🎾 Practice BUT still learning! Realistic expectation!"
        },
        {
          type: "mc",
          q: "She's very smart, _____ she works quite slowly.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' contrasts traits! Smart BUT slow worker!",
          successtip: "✓ BUT! Intelligence ≠ speed! 🧠 Smart BUT methodical! Different qualities!"
        },
        {
          type: "mc",
          q: "I'm good at my job, _____ I don't earn very much.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' shows unfair situation! Good work BUT low pay!",
          successtip: "✓ BUT! Wage frustration! 💼 Good performance BUT poor pay! Worker reality!"
        },
        {
          type: "mc",
          q: "He speaks really well, _____ he's very shy.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' contrasts ability and personality! Speaks well BUT shy!",
          successtip: "✓ BUT! Personality vs skill! 🗣️ Good speaker BUT shy! Interesting contrast!"
        },
        {
          type: "mc",
          q: "I can understand quite well, _____ I can't speak fluently.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' shows learning gap! Understand BUT can't speak!",
          successtip: "✓ BUT! Passive vs active! 👂 Listening ahead of speaking! Every learner knows!"
        },
        {
          type: "mc",
          q: "She works very hard, _____ she's really happy.",
          options: ["but", "so", "and"],
          a: "and",
          tip: "'And' shows addition! Hard work AND happiness together!",
          successtip: "✓ AND! Positive combination! 💪😊 Hard work AND happy! Work satisfaction!"
        }
      ]
    },

    // ===================================================================
    // SET 9: Complex Sentences - 2-3 clauses (10 preguntas B1+)
    // ===================================================================
    {
      id: "m3-u14-set9",
      title: "Complex Sentences B1",
      questions: [
        {
          type: "mc",
          q: "I practice every day, _____ I can speak _____ well now.",
          options: ["so / quite", "because / very", "but / really"],
          a: "so / quite",
          tip: "Practice (action) → speak quite well (result)! Two ideas!",
          successtip: "✓ SO + QUITE! B1 mastery! 💪 Action → moderate result! Native sentence structure!"
        },
        {
          type: "mc",
          q: "I'm good at cooking _____ I practice daily, _____ I work in a restaurant.",
          options: ["because / so", "so / because", "but / so"],
          a: "because / so",
          tip: "Reason (because practice) → Result (so work there)! Three clauses!",
          successtip: "✓ BECAUSE + SO! Three ideas! 🔥 Reason → Action → Result! Advanced logic!"
        },
        {
          type: "mc",
          q: "She can read _____ well, _____ she can't speak very fast.",
          options: ["quite / but", "very / so", "really / because"],
          a: "quite / but",
          tip: "Moderate skill + contrast! Read ≠ speak! Two-clause sentence!",
          successtip: "✓ QUITE + BUT! Common gap! 📚 Reading ahead of speaking! B1 honesty!"
        },
        {
          type: "mc",
          q: "I'm _____ tired _____ I worked 12 hours, _____ I walk slowly.",
          options: ["really / because / so", "very / so / because", "quite / but / so"],
          a: "really / because / so",
          tip: "Feeling + reason + consequence! Three-part logic!",
          successtip: "✓ REALLY + BECAUSE + SO! Triple logic! 😴 State → reason → effect! Native thinking!"
        },
        {
          type: "mc",
          q: "I can understand English _____ well, _____ I can't write _____.",
          options: ["quite / but / well", "very / so / good", "really / because / well"],
          a: "quite / but / well",
          tip: "Moderate + contrast + negative! Understanding ≠ writing!",
          successtip: "✓ QUITE + BUT + WELL! Skill gap! ✍️ Understand BUT can't write! Learning reality!"
        },
        {
          type: "mc",
          q: "She's _____ good at her job _____ she works carefully, _____ her boss loves her.",
          options: ["really / because / so", "very / so / because", "quite / but / so"],
          a: "really / because / so",
          tip: "Quality + reason + result! Perfect chain!",
          successtip: "✓ REALLY + BECAUSE + SO! Perfect chain! 💼 Careful work = boss approval! Success story!"
        },
        {
          type: "mc",
          q: "I'm bad at sports _____ I can't coordinate well, _____ I don't play.",
          options: ["because / so", "so / because", "but / so"],
          a: "because / so",
          tip: "Weakness + reason + consequence! Logical flow!",
          successtip: "✓ BECAUSE + SO! Logical avoidance! ⚽ Can't coordinate = don't play! Smart choice!"
        },
        {
          type: "mc",
          q: "He speaks _____ well _____ he practices, _____ he's still shy.",
          options: ["quite / because / but", "very / so / because", "really / but / so"],
          a: "quite / because / but",
          tip: "Skill + reason + personality contrast! Complex person!",
          successtip: "✓ QUITE + BECAUSE + BUT! Full picture! 🗣️ Skill + reason + personality! B1 complexity!"
        },
        {
          type: "mc",
          q: "I can cook _____ well, _____ I learned from my mom, _____ I save money.",
          options: ["quite / because / so", "very / so / because", "really / but / so"],
          a: "quite / because / so",
          tip: "Skill + origin + practical benefit! Complete story!",
          successtip: "✓ QUITE + BECAUSE + SO! Life story! 👨‍🍳 Mom taught = save money! Practical wisdom!"
        },
        {
          type: "mc",
          q: "She's _____ tired, _____ she can work _____ carefully.",
          options: ["quite / but / very", "very / so / quite", "really / because / very"],
          a: "quite / but / very",
          tip: "State + contrast + dedication! Work ethic!",
          successtip: "✓ QUITE + BUT + VERY! Professionalism! 💪 Tired BUT maintains standards! Work pride!"
        }
      ]
    },

    // ===================================================================
    // SET 10: Native Expressions B1 (10 preguntas)
    // ===================================================================
    {
      id: "m3-u14-set10",
      title: "Native Expressions",
      questions: [
        {
          type: "mc",
          q: "I'm _____ good at customer service because I'm polite, _____ customers like me.",
          options: ["really / so", "very / because", "quite / but"],
          a: "really / so",
          tip: "Job skill + reason + result! Interview answer!",
          successtip: "✓ REALLY + SO! Interview gold! 💼 Polite = customers like! Hire-worthy answer!"
        },
        {
          type: "mc",
          q: "I can speak English _____ well, _____ I still make mistakes.",
          options: ["quite / but", "very / so", "really / because"],
          a: "quite / but",
          tip: "Honest self-assessment! Good but not perfect!",
          successtip: "✓ QUITE + BUT! Honest learner! 📚 Good but improving! Job interview honesty!"
        },
        {
          type: "mc",
          q: "She works _____ hard _____ she's tired, _____ she never complains.",
          options: ["really / because / but", "very / so / because", "quite / but / so"],
          a: "really / because / but",
          tip: "Intensity + reason + contrast! Strong work ethic!",
          successtip: "✓ REALLY + BECAUSE + BUT! Hustle culture! 💪 Tired BUT no complaints! Immigrant grit!"
        },
        {
          type: "mc",
          q: "I'm bad at remembering names, _____ I write everything down.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' shows solution! Weakness → strategy!",
          successtip: "✓ SO! Practical solution! 📝 Bad memory = write things! Smart adaptation!"
        },
        {
          type: "mc",
          q: "He can type _____ quickly, _____ he makes mistakes sometimes.",
          options: ["quite / but", "very / so", "really / because"],
          a: "quite / but",
          tip: "Speed + accuracy contrast! Common trade-off!",
          successtip: "✓ QUITE + BUT! Speed vs accuracy! ⌨️ Fast BUT errors! Realistic assessment!"
        },
        {
          type: "mc",
          q: "I understand English _____ well now, _____ I watch TV every day.",
          options: ["quite / because", "very / so", "really / but"],
          a: "quite / because",
          tip: "Progress + method! Learning strategy revealed!",
          successtip: "✓ QUITE + BECAUSE! Learning hack! 📺 TV watching = understanding! Study method!"
        },
        {
          type: "mc",
          q: "She's _____ good at her job, _____ she doesn't earn enough money.",
          options: ["really / but", "very / so", "quite / because"],
          a: "really / but",
          tip: "Performance + unfair pay! Worker frustration!",
          successtip: "✓ REALLY + BUT! Wage gap! 💰 Good work BUT low pay! Working class reality!"
        },
        {
          type: "mc",
          q: "I can drive, _____ I'm not _____ good at parking.",
          options: ["but / very", "so / quite", "because / really"],
          a: "but / very",
          tip: "General skill + specific weakness! Common driver issue!",
          successtip: "✓ BUT + NOT VERY! Parking fear! 🚗 Drive BUT can't park! Universal struggle!"
        },
        {
          type: "mc",
          q: "He speaks _____ quietly _____ he's shy, _____ people like him.",
          options: ["quite / because / but", "very / so / because", "really / but / so"],
          a: "quite / because / but",
          tip: "Behavior + reason + positive result! Personality analysis!",
          successtip: "✓ QUITE + BECAUSE + BUT! Personality! 🤫 Shy BUT likeable! Quiet charm!"
        },
        {
          type: "mc",
          q: "I'm _____ happy with my English now _____ I can communicate well!",
          options: ["really / because", "very / so", "quite / but"],
          a: "really / because",
          tip: "Achievement + reason! Learning milestone!",
          successtip: "✓ REALLY + BECAUSE! Learning pride! 🎉 Communicate well = really happy! Success!"
     }
      ]
    }
  ],

readingSet: {
  id: "m3-u14-reading",
  title: "Reading: B1 Complex Connections",
  category: "reading",

  questions: [

    {
      type: "passage",
      passage: `**Customer Service Excellence**

Maria is really good at customer service because she speaks quite well and she's very polite, so customers love her. She is a very valuable member of the retail team.`
    },
    {
      type: "mc",
      q: "Why is Maria good at customer service?",
      options: [
        "Because she speaks well and is polite",
        "Because customers love her",
        "Because she works hard"
      ],
      a: "Because she speaks well and is polite",
      tip: "El conector 'Because' introduce la RAZÓN. Busca las dos habilidades mencionadas.",
      successtip: "✓ B1 REASONING!\nHas identificado las dos habilidades (speaks well + polite) que explican su éxito. 🗣️✨"
    },
    {
      type: "mc",
      q: "What is the result of Maria's skills?",
      options: ["Customers love her", "She speaks well", "She's polite"],
      a: "Customers love her",
      tip: "El conector 'So' introduce el RESULTADO o consecuencia de sus actos.",
      successtip: "✓ RESULT IDENTIFIED!\nSus habilidades producen una consecuencia positiva: el cariño de los clientes. 💕"
    },
  {
      type: "passage",
      passage: `**The Language Gap**

I practice English every day, so I can understand quite well now, but I still can't speak very fast. It is a long process, but I am really happy with my progress.`
    },
    {
      type: "mc",
      q: "Why can the person understand English well now?",
      options: [
        "Because they practice every day",
        "Because they speak fast",
        "Because they can't speak"
      ],
      a: "Because they practice every day",
      tip: "Busca la acción constante que realiza el narrador para mejorar.",
      successtip: "✓ DAILY PRACTICE!\nLa práctica diaria es la causa de su buena comprensión. 📚"
    },
    {
      type: "mc",
      q: "What contrast does the word 'but' show in the first sentence?",
      options: [
        "Understand well BUT can't speak fast",
        "Practice BUT understand",
        "Every day BUT now"
      ],
      a: "Understand well BUT can't speak fast",
      tip: "El 'But' separa una habilidad que ya domina de una que aún le cuesta.",
      successtip: "✓ SKILL CONTRAST!\nEs común entender (listening) mejor de lo que se habla (speaking). 👂 vs 🗣️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Athletic Limitations (preguntas 5-7)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Sports and Coordination**

He's bad at sports because he can't coordinate well, so he doesn't play soccer, but he can run quite fast. He prefers individual activities over team games.`
    },
    {
      type: "mc",
      q: "Why is he bad at sports?",
      options: [
        "Because he can't coordinate well",
        "Because he doesn't play soccer",
        "Because he runs fast"
      ],
      a: "Because he can't coordinate well",
      tip: "'Because' nos da la explicación física de su problema.",
      successtip: "✓ PHYSICAL REASON!\nLa falta de coordinación es la raíz de su dificultad en los deportes. 🎯"
    },
    {
      type: "mc",
      q: "What is the consequence (so) of him being bad at sports?",
      options: ["He doesn't play soccer", "He can't coordinate", "He runs fast"],
      a: "He doesn't play soccer",
      successtip: "✓ LOGICAL CONSEQUENCE!\nComo no se le dan bien, prefiere evitar deportes como el fútbol. ⚽❌"
    },
    {
      type: "mc",
      q: "What positive contrast does 'but' show here?",
      options: [
        "He is bad at sports BUT he can run quite fast",
        "He can't coordinate BUT he doesn't play",
        "He runs fast BUT he is bad"
      ],
      a: "He is bad at sports BUT he can run quite fast",
      successtip: "✓ POSITIVE TWIST!\nAunque no es coordinado, tiene una habilidad física destacada: la velocidad. 🏃"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Fatigue and Modifiers (preguntas 8-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Long Day**

I'm really tired today because I worked very hard yesterday, so I walk quite slowly now. I just want to get home and sleep for many hours.`
    },
    {
      type: "mc",
      q: "How many ideas are connected in a chain in this sentence?",
      options: [
        "Three (tired because worked, so walk slow)",
        "Two",
        "One"
      ],
      a: "Three (tired because worked, so walk slow)",
      successtip: "✓ COMPLEX CHAIN!\nHas seguido el hilo: Trabajo duro -> Cansancio -> Caminar lento. 💪😴🚶"
    },
    {
      type: "mc",
      q: "What modifier describes the level of tiredness?",
      options: ["Really", "Very", "Quite"],
      a: "Really",
      tip: "'Really' enfatiza la intensidad del estado físico.",
      successtip: "✓ EMPHASIS!\n'Really tired' indica un nivel alto de agotamiento. 😴"
    },
    {
      type: "mc",
      q: "What modifier describes the walking speed?",
      options: ["Quite slowly", "Very slowly", "Really slowly"],
      a: "Quite slowly",
      tip: "'Quite' indica un nivel moderado, no extremo.",
      successtip: "✓ MODERATION!\n'Quite slowly' significa que camina lento, pero no a un nivel exagerado. 🚶"
    }
  ]
},
    reviewset: {      
      id: "m3-u14-review",
      title: "Unit 14 Review B1",
      questions: [
        {
          type: "match",
          q: "Final Review: Match modifiers to intensity",
          pairs: [
            { left: "very (neutral)", right: "High intensity" },
            { left: "really (emphatic)", right: "Very high + emotion" },
            { left: "quite (moderate)", right: "Medium-high" }
          ],
          a: "completed",
          tip: "Remember intensity levels!",
          successtip: "✓ Intensity mastered! Very = strong, Really = stronger + emotion, Quite = moderate! 🎯"
        },
        {
          type: "match",
          q: "Match connectors to function",
          pairs: [
            { left: "BECAUSE", right: "Shows reason/cause" },
            { left: "SO", right: "Shows result/consequence" },
            { left: "BUT", right: "Shows contrast/difference" }
          ],
          a: "completed",
          tip: "Each connector has a job!",
          successtip: "✓ Connector logic! BECAUSE = why, SO = result, BUT = contrast! B1 thinking! 🧠"
        },
        {
          type: "mc",
          q: "I'm good at swimming. I can swim _____ well.",
          options: ["quite", "quiet", "good"],
          a: "quite",
          tip: "Good at + can + modifier!",
          successtip: "✓ QUITE well! Core lesson! 🏊 Good at = can do quite well! Native expression!"
        },
        {
          type: "mc",
          q: "I'm bad at skiing _____ I can't coordinate.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains WHY you're bad!",
          successtip: "✓ BECAUSE! Reason! 🎿 Can't coordinate = reason for weakness! Logic!"
        },
        {
          type: "mc",
          q: "I practice every day, _____ I can speak _____ well now.",
          options: ["so / quite", "because / very", "but / really"],
          a: "so / quite",
          tip: "Practice → result! Two-clause sentence!",
          successtip: "✓ SO + QUITE! B1 mastery! 💪 Action → moderate result! Progress!"
        },
        {
          type: "mc",
          q: "I can read well, _____ I can't speak fast.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' shows the contrast between skills!",
          successtip: "✓ BUT! Contrast! 📚 Read ≠ speak! Every learner's reality!"
        },
        {
          type: "input",
          q: "Complete with THREE words: I'm _____ tired _____ I worked hard, _____ I walk slowly.",
          a: ["really", "because", "so"],
          accept: { ignoreCase: true, ignoreOrder: false },
          tip: "State → reason → consequence! Three parts!",
          successtip: "✓ REALLY + BECAUSE + SO! Triple logic! 😴 Three-part sentence mastered! B1 achieved!"
        },
        {
          type: "mc",
          q: "She's _____ good at English _____ she practices every day.",
          options: ["really / because", "very / so", "quite / but"],
          a: "really / because",
          tip: "Emphasis + reason! Why is she so good?",
          successtip: "✓ REALLY + BECAUSE! High skill + reason! 📚 Practice explains excellence!"
        },
        {
          type: "mc",
          q: "I'm good at my job _____ I work carefully, _____ my boss is happy.",
          options: ["because / so", "so / because", "but / so"],
          a: "because / so",
          tip: "Reason + result! Why good → what happens!",
          successtip: "✓ BECAUSE + SO! Work chain! 💼 Careful work → happy boss! Success formula!"
        },
        {
          type: "mc",
          q: "Choose the B1 sentence:",
          options: [
            "I practice daily, so I speak quite well, but I'm still learning.",
            "I practice English.",
            "I speak well."
          ],
          a: "I practice daily, so I speak quite well, but I'm still learning.",
          tip: "B1 = multiple clauses with connectors!",
          successtip: "✓ B1 MASTERED! 🔥 Three clauses! Action + result + reality! You reached B1! Congratulations! 🎉🚀"
        }
      ]
    }
};
